import { ShieldCheck, CheckCircle, DollarSign, Home, ThumbsUp, Ruler, CalendarClock, Hammer, Users, Wrench } from 'lucide-react';

import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import Split from '../../components/Split';
import CTASection from '../../components/CTASection';
import Benefits from '../../components/Benefits';
import ContentBlock from '../../components/ContentBlock';
import ProcessRoadmap from '../../components/ProcessRoadmap';
import FAQ from '../../components/FAQ';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';
import QuickQuoteForm from '../../components/QuickQuoteForm';
import { generateServiceSchema } from '../../utils/generateServiceSchema';
import { generateWebPageSchema } from '../../utils/generateWebPageSchema';
import { generateFAQSchema } from '../../utils/generateFAQSchema';
import FAQData from '../../data/faq/earthquake-retrofit.js';



export default function earthquakeRetrofit() {
  const service = {
    title: 'Earthquake Retrofitting',
    image: '/hero/best-earthquake-retrofit-services-in-los-angeles.webp',
    description: 'Seismic retrofitting by Modern Build. Protect your home with code-compliant, engineered solutions. Free estimate: (323) 300-4130',
    link: '/services/earthquake-retrofit',
  };

  const baseUrl = 'https://modernbuildremodel.com';
  const pageUrl = `${baseUrl}${service.link}`;

  const serviceSchema = generateServiceSchema(service, pageUrl);

  // Default location for static service page
  const defaultLocation = ' in Los Angeles, CA';

  // Replace <IN_CITY_ZIP> placeholders in the FAQ answers
  const mergedFaqs = FAQData.map(faq => ({
    ...faq,
    question: faq.question.replace(/<IN_CITY_ZIP>/g, defaultLocation),
    answer: faq.answer.replace(/<IN_CITY_ZIP>/g, defaultLocation),
  }));

      // Breadcrumb JSON-LD
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": baseUrl },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": `${baseUrl}/services` },
      { "@type": "ListItem", "position": 3, "name": "Earthquake Retrofit Services", "item": pageUrl }
    ]
  };

  return (
    <div className="bg-white">
    <>
      <Head>
        <title>Earthquake Retrofitting Services | Free Estimate | Modern Build</title>
        <meta name="title" content="Earthquake Retrofitting Services | Free Estimate | Modern Build" />
        <meta name="description" content="Seismic retrofitting by Modern Build. Protect your home with code-compliant, engineered solutions. Free estimate: (323) 300-4130" />
        <meta name="keywords" content="earthquake retrofitting, los angeles seismic retrofit, foundation bolting, cripple wall bracing, los angeles retrofit contractor, soft story retrofit, seismic upgrades, modern build" />
        <meta name="author" content="Modern Build Home Remodeling" />
        <link rel="canonical" href="https://modernbuildremodel.com/services/earthquake-retrofit" />

        <meta property="og:title" content="Earthquake Retrofitting Services | Free Estimate | Modern Build" />
        <meta property="og:description" content="Seismic retrofitting by Modern Build. Protect your home with code-compliant, engineered solutions. Free estimate: (323) 300-4130" />
        <meta property="og:image" content="https://modernbuildremodel.com/hero/best-earthquake-retrofit-services-in-los-angeles.webp" />
        <meta property="og:url" content="https://modernbuildremodel.com/services/earthquake-retrofit" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Earthquake Retrofitting Services | Free Estimate | Modern Build" />
        <meta name="twitter:description" content="Seismic retrofitting by Modern Build. Protect your home with code-compliant, engineered solutions. Free estimate: (323) 300-4130" />
        <meta name="twitter:image" content="https://modernbuildremodel.com/hero/best-earthquake-retrofit-services-in-los-angeles.webp" />
        <meta name="twitter:site" content="@modernbuildhome" />


        {/* JSON-LD: WebPage */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: 'Earthquake Retrofitting Services | Free Estimate | Modern Build',
                description: 'Expert earthquake retrofitting services in Los Angeles. Modern Build helps secure your home against seismic activity with engineered, code-compliant solutions.',
                slug: '/services/earthquake-retrofit'
              })
            )
          }}
        />

        {/* JSON-LD: Breadcrumb */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

        {/* JSON-LD: Service */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />        

        {/* JSON-LD: FAQ */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateFAQSchema({
            service: service.title,
            city: 'Studio City',
            zip: '91604',
            faqs: mergedFaqs,
          }))
        }} />
      </Head>

      <Header />
      <Hero category="earthquakeRetrofit" page="page" hideButton={true} hasContentH1={false}/>

      <QuickQuoteForm serviceTitle={service.title} />

      <Split
        subtitle="EARTHQUAKE RETROFITTING SERVICES"
        title="Protect Your Home From Seismic Risk"
        background="white"
        description="Modern Build specializes in earthquake retrofitting for homes in Los Angeles. Our expert team reinforces your foundation, cripple walls, and framing to reduce damage during an earthquake and meet city retrofit requirements."
        extraText="We offer free inspections, engineering plans, permitting, and complete structural upgrades for both single-family homes and soft-story buildings."
        phone="(323) 300-4130"
        image1="/split/earthquake-retrofitting-los-angeles-foundation.webp"
        image2="/split/seismic-retrofit-los-angeles-ca.webp"
      />

      <Benefits
        title="What’s Included In an Earthquake Retrofit?"
        subtitle="Code-compliant, professional seismic upgrades"
        items={[
          {
            icon: <ShieldCheck className="w-8 h-8 text-primary" />,
            title: "Foundation Bolting & Anchoring",
            href: "/services/foundations",
          },
          {
            icon: <Hammer className="w-8 h-8 text-primary" />,
            title: "Cripple Wall Bracing",
          },
          {
            icon: <Ruler className="w-8 h-8 text-primary" />,
            title: "Engineer-Designed Retrofit Plans",
          },
          {
            icon: <Wrench className="w-8 h-8 text-primary" />,
            title: "Soft-Story Reinforcement",
          },
          {
            icon: <CheckCircle className="w-8 h-8 text-primary" />,
            title: "City Permits & Inspections",
          },
          {
            icon: <DollarSign className="w-8 h-8 text-primary" />,
            title: "Financing Options Available",
            href: "/financing",
          },
          {
            icon: <Home className="w-8 h-8 text-primary" />,
            title: "Single-Family & Multi-Unit Buildings",
            href: "/services/complete-home-remodeling",
          },
          {
            icon: <ThumbsUp className="w-8 h-8 text-primary" />,
            title: "Work Performed by Licensed, Bonded & Insured Contractors",
            href: "/about-us",
          },
        ]}
      />

      <ContentBlock
        subtitle="LICENSED LOS ANGELES RETROFIT EXPERTS"
        title="Why Earthquake Retrofitting Matters"
        description="Thousands of homes in Los Angeles were built before modern seismic codes. Without retrofitting, these homes are vulnerable to major structural damage. Modern Build helps you safeguard your property, meet local laws, and improve your home’s long-term value."
        extraText="We handle structural assessments, engineering, permits, and construction — so you get peace of mind, start to finish."
        buttonText="Schedule a Free Inspection"
        background="white"
      />

      <ProcessRoadmap
        title="Our Earthquake Retrofit Process"
        subtitle="From structural inspection to final approval"
        steps={[
          {
            icon: <Users className="w-10 h-10 text-primary" />,
            title: "Free Home Evaluation",
            description: "We assess your home’s foundation and framing to determine seismic vulnerabilities.",
          },
          {
            icon: <Ruler className="w-10 h-10 text-primary" />,
            title: "Engineering & Permits",
            description: "Our engineers design a retrofit plan that meets city code, and we handle all permitting.",
          },
          {
            icon: <CalendarClock className="w-10 h-10 text-primary" />,
            title: "Seismic Retrofit Installation",
            description: "We complete bolting, bracing, and reinforcements using top-grade materials and experienced crews.",
          },
          {
            icon: <CheckCircle className="w-10 h-10 text-primary" />,
            title: "Final Inspection & Compliance",
            description: "We walk you through the completed work and coordinate the city's final inspection and sign-off.",
          },
        ]}
      />

      <Split
        subtitle="PEACE OF MIND, ENGINEERED"
        title="Protect Your Family & Investment"
        background="white"
        description="Your home is one of your biggest investments — and in earthquake-prone Los Angeles, seismic safety is critical. Retrofitting is one of the smartest and most affordable ways to protect your property and loved ones."
        extraText="We bring experience, city compliance, and a worry-free process to every project."
        phone="(323) 300-4130"
        image1="/split/seismic-retrofitting-los-angeles-homes.webp"
        image2="/split/foundation-retrofit-los-angeles.webp"
        reverse={true}
      />

      <CTASection
        title="Secure Your Home Today"
        subtitle="Free Seismic Inspection + 0% Financing"
        buttonText="Get a Free Retrofit Estimate"
        link="/contact"
        backgroundImage="/hero/best-home-remodling-contractor-in-los-angeles.webp"
      />

      <FAQ category="earthquake-retrofit" />
      <SocialProof />
      <ContactUs />
      <Footer />
    </>
    </div>
  );
}
